// Layer 3 (Transformation) — the class reliability roll-up (a20-maintenance S3).
// Pure: no React, no engine mutations, no formatting, no rounding, no money.
//
// `rows` is always the full YTD event log (`downtimeEvents()`), passed in by the
// caller — these functions never call `downtimeEvents()` themselves, so the tab
// cuts the same log the Causes Pareto reads without a second projection.
//
// durable: class reliability roll-up over per-item KPIs and a stoppage log (ADR-0012).

import type { SeriesTime } from '@/versions/v1/schema/reading-series';
import { addMinutes, diffMinutes, formatDayMonth } from '@/versions/v1/shared/clock.derive';
import { RISK_ENTRIES } from '@/versions/v1/modules/risk/seed';
import { BASIS_END, type DowntimeItemId } from '../maintenance/downtime.seed';
import { EQUIPMENT_CLASSES, MTBF_WARN_PCT, TREND_STABLE_BAND_PCT } from '../maintenance/downtime-events.seed';
import { itemKpis } from '../maintenance/Downtime.transform';
import type { DowntimeEvent } from '../maintenance/DowntimeEvents.transform';
import { MAINTAINABLE_ITEMS } from '../maintenance/strategy.seed';

export type ReliabilityTrend = 'improving' | 'worsening' | 'stable';
export type MtbfBand = 'ok' | 'warn' | 'bad';

export type ReliabilityRow = {
  classId: string;
  label: string;
  itemIds: readonly DowntimeItemId[];
  /** Σ itemKpis(id, days).breakdownCount over the members. */
  eventCount: number;
  /** Σ runHours ÷ Σ breakdownCount; Σ runHours alone when eventCount === 0 (itemKpis' own convention). */
  actualMtbfHours: number;
  targetMtbfHours: number;
  /** targetMtbfHours − actualMtbfHours (negative when above target). */
  gapHours: number;
  /** actualMtbfHours ÷ targetMtbfHours × 100 — unrounded. */
  pctOfTarget: number;
  band: MtbfBand;
  /** Σ repairHours ÷ Σ breakdownCount; null when eventCount === 0. */
  actualMttrHours: number | null;
  targetMttrHours: number;
  /** Highest rpn among members that carry a riskId (RISK_ENTRIES, never typed); undefined when none does. */
  rpn: number | undefined;
  trend: ReliabilityTrend;
  /** End of the class's last unplanned event in `rows` + actualMtbfHours; null when the class has no unplanned event. */
  expectedNext: SeriesTime | null;
};

export type ReliabilityKpis = {
  classCount: number;
  /** rows with band === 'ok'. */
  atTargetCount: number;
  /** rows[0] — the worst pctOfTarget. */
  worst: ReliabilityRow;
  /** rows[rows.length − 1] — the best. */
  best: ReliabilityRow;
  /** rows with a measured MTTR (no repair this basis → null, not zero). */
  mttrMeasuredCount: number;
  /** Of those, the ones at or under their class's MTTR target. */
  mttrWithinTargetCount: number;
  /** '09 May – 07 Jun' for days = 30. */
  basisLabel: string;
};

/** Rows with `start >= from && end <= to`, the same predicate `downtimeEvents`
 *  applies — so a stoppage straddling a window edge is in neither window,
 *  consistent with every other window read in this module. */
function within(
  rows: readonly DowntimeEvent[],
  itemIds: readonly DowntimeItemId[],
  from: SeriesTime,
  to: SeriesTime,
): DowntimeEvent[] {
  return rows.filter(
    (row) =>
      itemIds.includes(row.itemId) &&
      diffMinutes(row.start, from) >= 0 &&
      diffMinutes(row.end, to) <= 0,
  );
}

/** The band a class's MTBF sits in: at target `ok`, at or above MTBF_WARN_PCT
 *  of target `warn`, below it `bad`. */
function bandFor(pctOfTarget: number): MtbfBand {
  if (pctOfTarget >= 100) return 'ok';
  if (pctOfTarget >= MTBF_WARN_PCT) return 'warn';
  return 'bad';
}

/** One class's MTBF over one window measured on the LOG alone: the members'
 *  window hours less their every stoppage, over their unplanned count (the
 *  available hours alone when the count is 0). */
function logMtbfHours(memberCount: number, windowRows: readonly DowntimeEvent[], windowMinutes: number): number {
  const availableHours = (windowMinutes / 60) * memberCount;
  const loggedHours = windowRows.reduce((sum, row) => sum + row.hours, 0);
  const unplanned = windowRows.filter((row) => row.kind === 'unplanned').length;
  return unplanned > 0 ? (availableHours - loggedHours) / unplanned : availableHours - loggedHours;
}

/** The class-level reliability read: every EQUIPMENT_CLASSES entry rolled up
 *  from its members' `itemKpis` over the trailing `days` (default 30), ranked
 *  worst `pctOfTarget` first and tie-broken on label. The class targets are the
 *  only typed numbers in the projection; actual MTBF, actual MTTR, the gap, the
 *  band, the RPN and the trend are all computed here. */
export function reliabilityRows(rows: readonly DowntimeEvent[], days = 30): ReliabilityRow[] {
  const windowStart = addMinutes(BASIS_END, -days * 24 * 60);
  const previousStart = addMinutes(BASIS_END, -2 * days * 24 * 60);
  const windowMinutes = diffMinutes(BASIS_END, windowStart);

  const ranked = EQUIPMENT_CLASSES.map((cls) => {
    const kpis = cls.itemIds.map((itemId) => itemKpis(itemId, days));
    const eventCount = kpis.reduce((sum, kpi) => sum + kpi.breakdownCount, 0);
    const runHours = kpis.reduce((sum, kpi) => sum + kpi.runHours, 0);
    const repairHours = kpis.reduce((sum, kpi) => sum + kpi.repairHours, 0);

    // itemKpis' own convention: the available hours stand alone when nothing
    // broke down, and render like any other number — no floor flag.
    const actualMtbfHours = eventCount > 0 ? runHours / eventCount : runHours;
    const pctOfTarget = (actualMtbfHours / cls.targetMtbfHours) * 100;

    // Trend is read on ONE basis across both windows: the preceding window has
    // no interval data, only the log, so the direction comes from the log on
    // both sides. The level above stays on `itemKpis` so it reconciles with the
    // per-item MTBF captions the Downtime tab already publishes.
    const trailing = logMtbfHours(
      cls.itemIds.length,
      within(rows, cls.itemIds, windowStart, BASIS_END),
      windowMinutes,
    );
    const preceding = logMtbfHours(
      cls.itemIds.length,
      within(rows, cls.itemIds, previousStart, windowStart),
      windowMinutes,
    );
    const stableBand = preceding * (TREND_STABLE_BAND_PCT / 100);
    const trend: ReliabilityTrend =
      Math.abs(trailing - preceding) <= stableBand
        ? 'stable'
        : trailing > preceding
          ? 'improving'
          : 'worsening';

    // The register's own number, never typed: the highest rpn among the
    // members that carry a riskId — the register's tier bands are RPN bands, so
    // that member is the class's highest-criticality covered equipment.
    const rpns = MAINTAINABLE_ITEMS.filter(
      (item) => cls.itemIds.includes(item.id as DowntimeItemId) && item.riskId !== undefined,
    )
      .map((item) => RISK_ENTRIES.find((entry) => entry.id === item.riskId)?.rpn)
      .filter((rpn): rpn is number => rpn !== undefined);

    const unplanned = rows.filter(
      (row) => cls.itemIds.includes(row.itemId) && row.kind === 'unplanned',
    );
    const last = unplanned[unplanned.length - 1];

    return {
      classId: cls.id,
      label: cls.label,
      itemIds: cls.itemIds,
      eventCount,
      actualMtbfHours,
      targetMtbfHours: cls.targetMtbfHours,
      gapHours: cls.targetMtbfHours - actualMtbfHours,
      pctOfTarget,
      band: bandFor(pctOfTarget),
      actualMttrHours: eventCount > 0 ? repairHours / eventCount : null,
      targetMttrHours: cls.targetMttrHours,
      rpn: rpns.length > 0 ? Math.max(...rpns) : undefined,
      trend,
      expectedNext:
        last === undefined
          ? null
          : addMinutes(last.end, Math.round(actualMtbfHours * 60)),
    };
  });

  return ranked.sort(
    (a, b) => a.pctOfTarget - b.pctOfTarget || (a.label < b.label ? -1 : a.label > b.label ? 1 : 0),
  );
}

export function reliabilityKpis(rows: ReliabilityRow[], days = 30): ReliabilityKpis {
  const measured = rows.filter((row) => row.actualMttrHours !== null);
  return {
    classCount: rows.length,
    atTargetCount: rows.filter((row) => row.band === 'ok').length,
    worst: rows[0]!,
    best: rows[rows.length - 1]!,
    mttrMeasuredCount: measured.length,
    mttrWithinTargetCount: measured.filter((row) => row.actualMttrHours! <= row.targetMttrHours).length,
    // The end steps back one day exactly as the Causes window label does —
    // BASIS_END is the open day's start, so the last closed day is the day before.
    basisLabel: `${formatDayMonth(addMinutes(BASIS_END, -days * 24 * 60))} – ${formatDayMonth(addMinutes(BASIS_END, -24 * 60))}`,
  };
}

/** Chart series use the exact class rows the table consumes. */
export function reliabilityBarRows(rows: readonly ReliabilityRow[]): ReliabilityRow[] {
  return [...rows];
}

/** One scale for every class's MTBF bar, so bar length compares across rows:
 *  the largest actual or target MTBF in the roll-up. */
export function mtbfBarScale(rows: readonly ReliabilityRow[]): number {
  return Math.max(1, ...rows.map((row) => Math.max(row.actualMtbfHours, row.targetMtbfHours)));
}
